import { FlashList } from '@shopify/flash-list';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ChecklistTemplateItem,
  ChecklistTemplateX,
} from '@components/ProjectDetail/ProjectChecklist/ChecklistTemplateItem';
import { Box, Text } from '@components/Restyle/index';
import ActivityIndicatorLoading from '@components/shared/ActivityIndicatorLoading';
import Icon from '@components/shared/Icon/Icon';
import { useWebDrawer } from '@components/Web/Drawer/WebDrawerContext';
import { useListChecklistTemplatesQuery } from '@graphql/generated';
import { useChecklistTemplate } from '@hooks/useChecklistTemplate';
import theme from '@themes/theme';

import { ChecklistActionBar } from './ChecklistActionBar';
import { ChecklistCommonModal } from './ChecklistCommonModal';

export const ProjectChecklistTemplate: React.FC<{
  projectUUId: string;
}> = ({ projectUUId }: { projectUUId: string }) => {
  const { t } = useTranslation();

  // Use the shared checklist template hook
  const {
    expandedId,
    creatingChecklist,
    modalVisible,
    skippedItems,
    checklistItems,
    setChecklistItems,
    toggleExpand,
    toggleItemChecked,
    toggleItemCheckedAll,
    toggleFavorite,
    createChecklist,
    doCloseModal,
    totalCheckedCount,
    sortChecklistTemplates,
  } = useChecklistTemplate(projectUUId);

  const { setIdProjectChecklistTemplateOpen } = useWebDrawer();

  // Template-specific data fetching
  const { data: checklistTemplates, loading: loadingTemplate } =
    useListChecklistTemplatesQuery();

  // Load template data when available
  useEffect(() => {
    if (checklistTemplates?.listChecklistTemplates) {
      const templateList = checklistTemplates.listChecklistTemplates.map(
        (template) => ({
          ...template,
          itemsChecked: [],
        })
      );
      setChecklistItems(sortChecklistTemplates(templateList));
    }
  }, [checklistTemplates?.listChecklistTemplates]);

  // Render function for checklist items
  const renderItem = ({ item }: { item: ChecklistTemplateX }) => (
    <ChecklistTemplateItem
      marginHorizontal='l'
      item={item}
      isExpanded={expandedId === item.id}
      onToggleExpand={toggleExpand}
      onToggleFavorite={toggleFavorite}
      onToggleItemCheckedAll={toggleItemCheckedAll}
      onToggleItemChecked={toggleItemChecked}
    />
  );

  return (
    <>
      {modalVisible && (
        <ChecklistCommonModal
          visible={modalVisible}
          skippedItems={skippedItems}
          onClose={() => doCloseModal()}
        />
      )}
      <Box flex={1} backgroundColor='white'>
        <Box flexDirection='row' mx='l' mt='l' style={{ marginBottom: 20 }}>
          <Text mr='m' variant='heading2' color='textPrimary'>
            {t('models:projects.checklist.checklistTemplate')}
          </Text>

          <Box
            alignItems='flex-end'
            flexDirection='row'
            accessibilityLabel='Close'
            style={{ flex: 1, justifyContent: 'flex-end' }}>
            <Icon
              name='X'
              variant='l'
              onPress={() => {
                setIdProjectChecklistTemplateOpen('');
              }}
            />
          </Box>
        </Box>

        {/* Loading indicator */}
        {loadingTemplate && checklistItems.length === 0 && (
          <Box height={48}>
            <ActivityIndicatorLoading />
          </Box>
        )}

        {/* Checklist templates list */}
        {checklistItems.length > 0 ? (
          <FlashList
            data={checklistItems}
            extraData={[expandedId]}
            renderItem={renderItem}
            keyExtractor={(item) => item.id}
            estimatedItemSize={56}
            drawDistance={500}
            removeClippedSubviews={true}
            initialNumToRender={15}
            maxToRenderPerBatch={10}
            windowSize={21}
            contentContainerStyle={{
              backgroundColor: theme.colors.white,
              paddingBottom: 20, // Add some padding at the bottom
            }}
            showsVerticalScrollIndicator={false}
          />
        ) : (
          // Empty state when no templates are available

          <Box flex={1} justifyContent='center' alignItems='center' padding='m'>
            {!loadingTemplate && (
              <Text variant='body1' color='textSecondary' textAlign='center'>
                {t('models:projects.checklist.noTemplatesAvailable')}
              </Text>
            )}
          </Box>
        )}

        {/* Bottom action bar */}
        <ChecklistActionBar
          totalCheckedCount={totalCheckedCount}
          creatingChecklist={creatingChecklist}
          onCreateChecklist={() => {
            createChecklist();
          }}
          disabled={checklistItems.length === 0}
          marginBottom='l'
          marginHorizontal='l'
        />
      </Box>
    </>
  );
};
